/**
 * Pure helpers for the grouped-by-event audit log (BON-201 redesign).
 * No React imports — all logic is side-effect-free and unit-testable.
 */
import type { AuditCrewMember, AuditEntry } from '../../../../api/workshops';
import { crewPositionLabel } from '../../../../lib/crewPositions';

import { formatAuditDate, formatAuditScalar, formatAuditTime, formatPrice } from './workshopFields';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single rendered sub-row within an event block. */
export interface AuditSubRow {
  key: string;
  /** Small-caps label, e.g. "CREW", "DATE", "START", "TITLE". Empty string for
   *  the Status event (lifecycle single-change — no field label shown). */
  label: string;
  beforeStr: string;
  afterStr: string;
  /** Suffix appended to afterStr when the change is an add/remove. */
  suffix?: 'added' | 'removed';
}

/** Processed event ready to render. */
export interface AuditEventData {
  id: string;
  title: string;
  /** True when title already includes "· N changes" count. */
  hasCount: boolean;
  actorName: string;
  changedAt: string;
  subRows: AuditSubRow[];
  /** One plain sentence, for events better told than diffed. */
  summary?: string;
}

// ─── Field → section mapping ──────────────────────────────────────────────────

type Section = 'Basics' | 'Schedule' | 'Crew' | 'Status';

const FIELD_SECTION: Record<string, Section> = {
  capacity: 'Basics',
  priceCents: 'Basics',
  workshopTypeId: 'Basics',
  description: 'Basics',
  startsAt: 'Schedule',
  endsAt: 'Schedule',
  crew: 'Crew',
  lifecycle: 'Status',
};

function sectionOf(field: string): Section | null {
  return FIELD_SECTION[field] ?? null;
}

// ─── Crew position helpers ────────────────────────────────────────────────────

/** Name suffix in the audit trail: "Ada Lovelace (Teacher in training)". */
function positionTitle(position: string): string {
  return crewPositionLabel(position);
}

/** Sub-row badge text. Deliberately NOT the display label: these are the audit
 *  log's own short uppercase tokens, and `teacher` reads as CREW here. Left as
 *  its own vocabulary by BON-420 rather than folded into the label map. */
function positionLabel(position: string): string {
  if (position === 'teacher') return 'CREW';
  if (position === 'assistant') return 'ASSISTANT';
  return 'TRAINEE';
}

function isCrewArray(v: unknown): v is AuditCrewMember[] {
  return Array.isArray(v) && (v.length === 0 || (typeof v[0] === 'object' && 'name' in v[0]));
}

function crewDisplayName(m: AuditCrewMember): string {
  return `${m.name} (${positionTitle(m.position)})`;
}

// ─── Sub-row builders ─────────────────────────────────────────────────────────

function crewSubRows(
  key: string,
  before: AuditCrewMember[],
  after: AuditCrewMember[],
): AuditSubRow[] {
  // Collect all positions involved
  const positions = Array.from(
    new Set([...before.map((m) => m.position), ...after.map((m) => m.position)]),
  );

  return positions.map((pos) => {
    const bMember = before.find((m) => m.position === pos);
    const aMember = after.find((m) => m.position === pos);
    const label = positionLabel(pos);

    if (bMember && aMember) {
      // swap
      return {
        key: `${key}-${pos}`,
        label,
        beforeStr: crewDisplayName(bMember),
        afterStr: crewDisplayName(aMember),
      };
    }
    if (!bMember && aMember) {
      // add
      return {
        key: `${key}-${pos}`,
        label,
        beforeStr: '—',
        afterStr: `${aMember.name} added`,
        suffix: 'added' as const,
      };
    }
    // remove
    return {
      key: `${key}-${pos}`,
      label,
      beforeStr: bMember ? crewDisplayName(bMember) : '—',
      afterStr: '— removed',
      suffix: 'removed' as const,
    };
  });
}

function scheduleSubRows(
  key: string,
  field: 'startsAt' | 'endsAt',
  before: string,
  after: string,
): AuditSubRow[] {
  const rows: AuditSubRow[] = [];
  if (field === 'startsAt') {
    const beforeDate = formatAuditDate(before);
    const afterDate = formatAuditDate(after);
    if (beforeDate !== afterDate) {
      rows.push({ key: `${key}-date`, label: 'DATE', beforeStr: beforeDate, afterStr: afterDate });
    }
    const beforeTime = formatAuditTime(before);
    const afterTime = formatAuditTime(after);
    if (beforeTime !== afterTime) {
      rows.push({
        key: `${key}-start`,
        label: 'START',
        beforeStr: beforeTime,
        afterStr: afterTime,
      });
    }
  } else {
    const beforeTime = formatAuditTime(before);
    const afterTime = formatAuditTime(after);
    if (beforeTime !== afterTime) {
      rows.push({ key: `${key}-end`, label: 'END', beforeStr: beforeTime, afterStr: afterTime });
    }
  }
  return rows;
}

function scalarSubRow(
  key: string,
  field: string,
  before: unknown,
  after: unknown,
  hideLabel = false,
): AuditSubRow {
  let beforeStr: string;
  let afterStr: string;
  if (field === 'priceCents' && typeof before === 'number' && typeof after === 'number') {
    beforeStr = formatPrice(before);
    afterStr = formatPrice(after);
  } else {
    beforeStr = formatAuditScalar(field, before);
    afterStr = formatAuditScalar(field, after);
  }
  return { key, label: hideLabel ? '' : field.toUpperCase(), beforeStr, afterStr };
}

// ─── Field change → sub-rows ──────────────────────────────────────────────────

function subRowsForField(
  entryId: string,
  field: string,
  change: { before: unknown; after: unknown },
): AuditSubRow[] {
  const key = `${entryId}-${field}`;
  if (field === 'crew') {
    const before = isCrewArray(change.before) ? change.before : [];
    const after = isCrewArray(change.after) ? change.after : [];
    return crewSubRows(key, before, after);
  }
  if (field === 'startsAt' || field === 'endsAt') {
    return scheduleSubRows(key, field, String(change.before), String(change.after));
  }
  if (field === 'lifecycle') {
    // Status: no field label
    return [scalarSubRow(key, field, change.before, change.after, true)];
  }
  if (field === 'workshopTypeId') {
    // Resolved to display strings server-side; render as TITLE
    return [
      {
        key,
        label: 'TITLE',
        beforeStr: String(change.before ?? '—'),
        afterStr: String(change.after ?? '—'),
      },
    ];
  }
  return [scalarSubRow(key, field, change.before, change.after)];
}

// ─── Event title derivation ───────────────────────────────────────────────────

function crewPositionFromChange(change: { before: unknown; after: unknown }): string | null {
  const arr = isCrewArray(change.before)
    ? change.before
    : isCrewArray(change.after)
      ? change.after
      : null;
  if (!arr || arr.length === 0) return null;
  return arr[0].position;
}

export function deriveEventTitle(
  fields: string[],
  changes: Record<string, { before: unknown; after: unknown }>,
): string {
  const n = fields.length;
  if (n === 1) {
    const [field] = fields;
    if (field === 'lifecycle') return 'Status';
    if (field === 'crew') {
      const pos = crewPositionFromChange(changes[field]);
      if (pos) return positionTitle(pos);
      return 'Crew';
    }
    const section = sectionOf(field);
    return section ? `Edited ${section}` : 'Edited workshop';
  }

  // Multi-field: check if all fields belong to one section
  const sections = Array.from(new Set(fields.map(sectionOf).filter(Boolean)));
  if (sections.length === 1 && sections[0]) {
    return `Edited ${sections[0]} · ${n} changes`;
  }
  return `Edited workshop · ${n} changes`;
}

// ─── Main entry processor ─────────────────────────────────────────────────────

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** Older cancels recorded `movedCustomers` and no checkout count. */
function cancelledSummary(actorName: string, changes: Record<string, unknown>): string {
  const moved = Number(changes.bookingsMoved ?? changes.movedCustomers ?? 0);
  const checkouts = Number(changes.checkoutsCancelled ?? 0);
  const actor = actorName.replace(/\.$/, '');
  return `Workshop cancelled by ${actor}. Moved ${plural(moved, 'booking')} and cancelled ${plural(checkouts, 'in-flight checkout')}.`;
}

function isFieldChanges(v: unknown): v is Record<string, { before: unknown; after: unknown }> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function processAuditEntry(entry: AuditEntry): AuditEventData {
  const changes = isFieldChanges(entry.fieldChanges) ? entry.fieldChanges : {};
  const fields = Object.keys(changes);

  // Creation marker (BON-219): a workshop create is its own event type, not an
  // edit. Render a dedicated "Created workshop" header with no before→after
  // sub-row — a creation isn't a field change, so the marker is the whole story.
  if (entry.eventType === 'created') {
    return {
      id: entry.id,
      title: 'Created workshop',
      hasCount: false,
      actorName: entry.actorName,
      changedAt: entry.changedAt,
      subRows: [],
    };
  }

  if (entry.eventType === 'cancelled') {
    return {
      id: entry.id,
      title: 'Workshop cancelled',
      hasCount: false,
      actorName: entry.actorName,
      changedAt: entry.changedAt,
      subRows: [],
      summary: cancelledSummary(entry.actorName, changes),
    };
  }

  const title = fields.length === 0 ? 'Workshop' : deriveEventTitle(fields, changes);
  const hasCount = title.includes('· ') && title.includes('changes');

  const subRows = fields.flatMap((field) => subRowsForField(entry.id, field, changes[field]));

  return {
    id: entry.id,
    title,
    hasCount,
    actorName: entry.actorName,
    changedAt: entry.changedAt,
    subRows,
  };
}
